import { TranslateService } from '@ngx-translate/core';
import { MessageService } from '@app/service/message.service';
import { TrialBannerService } from '@app/shell/trial-banner/service/trial-banner.service';
import { Subscription, BehaviorSubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { LeaderboardHttpService } from '../../../service/http/http.service';
import { LeaderboardUsersInvites, LeaderboardUserInvite } from '../../../model/user.model';
import { tap } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';

@Injectable()
export class LeaderboardTrialInvitesPopupService {
  messageControl: UntypedFormControl;

  private _invitesData: BehaviorSubject<{
    loading: boolean;
    loaded: boolean;
    users: LeaderboardUserInvite[];
    closePopup: boolean;
  }> = new BehaviorSubject({
    loading: false,
    loaded: false,
    users: null,
    closePopup: false,
  });

  private subscriptions = new Subscription();

  constructor(
    private leaderboardHttpService: LeaderboardHttpService,
    private trialBannerService: TrialBannerService,
    private messageService: MessageService,
    private translateService: TranslateService,
  ) {}

  init() {
    this.getInvites();
  }

  destroy() {
    this.subscriptions.unsubscribe();
  }

  get invitesData$() {
    return this._invitesData.asObservable();
  }

  get invitesData() {
    return this._invitesData.value;
  }

  get invitesTrial$() {
    return this.trialBannerService.invitesTrial$;
  }

  get invitesTrial() {
    return this.trialBannerService.invitesTrial;
  }

  get usersToSentCount() {
    return this.invitesData.users.filter((user) => !user.sent).length;
  }

  addUser(user: LeaderboardUserInvite) {
    let newUsers = this.invitesData.users;
    newUsers.push(user);
    this._invitesData.next({
      ...this._invitesData.value,
      ...{
        users: newUsers,
      },
    });
  }

  removeUser(index: number) {
    let newUsers = this.invitesData.users;
    newUsers.splice(index, 1);
    this._invitesData.next({
      ...this._invitesData.value,
      ...{
        users: newUsers,
      },
    });
  }

  setClosePopup() {
    this._invitesData.next({
      ...this._invitesData.value,
      ...{
        closePopup: true,
      },
    });
  }

  setInvitesData(leaderboardUserInvites: LeaderboardUsersInvites) {
    this._invitesData.next({
      ...this._invitesData.value,
      ...{
        loaded: true,
        message: leaderboardUserInvites.email.message,
        users: leaderboardUserInvites.users,
      },
    });
  }

  setLoading(loading: boolean) {
    this._invitesData.next({
      ...this._invitesData.value,
      ...{
        loading: loading,
      },
    });
  }

  getInvites() {
    this.subscriptions.add(
      this.leaderboardHttpService
        .getLeaderboardInvitesTrial()
        .pipe(
          tap((invites: LeaderboardUsersInvites) => {
            invites.users.forEach((user) => (user.sent = true));
            this.messageControl = new UntypedFormControl(invites.email.message);
            this.setInvitesData(invites);
          }),
        )
        .subscribe(),
    );
  }

  sendInvites() {
    const filteredUsers = this.invitesData.users.filter((user) => !user.sent);
    if (filteredUsers.length === 0) {
      return;
    }
    const leaderboardUserInvites: LeaderboardUsersInvites = {
      users: filteredUsers,
      email: {
        message: this.messageControl.value,
      },
    };
    this.setLoading(true);
    this.subscriptions.add(
      this.leaderboardHttpService
        .sendLeaderboardInvitesTrial(leaderboardUserInvites)
        .pipe(
          tap((invites) => {
            const sentMessage = this.translateService.instant('v3-trial-invites-popup-invites-sent', {
              colleagues: filteredUsers.length,
            });
            this.messageService.setMessage(sentMessage, 'success');
            this.trialBannerService.setSentInvites(invites.sent);
            this.setClosePopup();
          }),
        )
        .subscribe(),
    );
  }
}
